import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { UsageMeter } from "@/shared/entitlements/components/usage-meter";
import { resolvePlanLabel } from "@/shared/entitlements/entitlements.constants";
import { useEntitlements } from "@/shared/entitlements/entitlements.context";
import { useCredits } from "@/shared/entitlements/hooks/use-credits";
import { useSeats } from "@/shared/entitlements/hooks/use-seats";

/**
 * Plan + usage section for account settings: the account's plan, AI credit
 * pool, and seat usage (W2 locked decision 3). Reads straight from the
 * entitlements context rather than taking props, the same pattern
 * `sidebar-workspace.tsx` uses for `useActiveWorkspace()`: a context hook
 * that already holds resolved data isn't a "fetch" in the components#C1
 * sense, so this can compose without the parent route wiring it through.
 * Credits/seats come from the `useCredits()` / `useSeats()` selector hooks
 * (same ones `sidebar-workspace.tsx` uses) rather than destructuring them
 * off `useEntitlements()` directly, for consistency across consumers.
 */
export function UsagePanel(): ReactNode {
  const { plan, isLoading } = useEntitlements();
  const credits = useCredits();
  const seats = useSeats();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Plan & usage</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {isLoading ? (
          <div className="flex flex-col gap-3" aria-hidden="true">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-text-secondary">Current plan</span>
              <span className="text-sm font-semibold text-text-primary">
                {resolvePlanLabel(plan)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <UsageMeter label="AI credits" used={credits.used} total={credits.grant} />
              {credits.remaining === null ? null : (
                <p className="text-xs text-text-tertiary">
                  {credits.remaining.toLocaleString()} credits remaining
                </p>
              )}
            </div>
            <UsageMeter label="Seats" used={seats.used} total={seats.cap} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
